import React, { useEffect, useState } from "react";
import api from "common/api";
import { Button } from "react-bootstrap";
import { toast } from "react-toastify";
import { ISubject, SubjectKind } from "common/types";
import Modal from "react-bootstrap/Modal";
import SubjectPictureUpload from "./SubjectPictureUpload";
import * as Sentry from "@sentry/react";

interface IEditSubjectProps {
  show: boolean;
  onHide: () => void;
  subject: ISubject;
  onSubjectUpdate: (subject: ISubject) => void;
}

interface ILessonPrice {
  id?: number;
  duration: number;
  price: string;
}

const EditSubjectModal: React.FC<IEditSubjectProps> = ({
  show,
  onHide,
  subject,
  onSubjectUpdate,
}) => {
  const [name, setName] = useState(subject.name);
  const [prepPrice, setPrepPrice] = useState(subject.prep_price);
  const [lessonPrices, setLessonPrices] = useState<ILessonPrice[]>([
    { duration: 60, price: "" },
    { duration: 90, price: "" },
    { duration: 120, price: "" },
  ]);

  useEffect(() => {
    setName(subject.name);
    setPrepPrice(subject.prep_price);

    if (subject.prices && subject.prices.length > 0) {
      const initialPrices = [
        { id: 0, duration: 60, price: "" },
        { id: 1, duration: 90, price: "" },
        { id: 2, duration: 120, price: "" },
      ];

      subject.prices.forEach((price) => {
        const index = initialPrices.findIndex(
          (p) => p.duration === price.duration,
        );
        if (index !== -1) {
          initialPrices[index].price = price.price;
          initialPrices[index].id = price.id;
        }
      });

      setLessonPrices(initialPrices);
    }
  }, [subject]);

  const handleNameChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setName(event.target.value);
  };

  const handlePriceChange = (index: number, value: string) => {
    const newPrices = [...lessonPrices];
    newPrices[index].price = value;
    setLessonPrices(newPrices);
  };

  const handleUpdate = async () => {
    if (!name) {
      toast.error("Unesite naziv predmeta");
      return;
    }

    if (subject.kind === SubjectKind.PREPARATION && !prepPrice) {
      toast.error("Unesite cijenu pripreme");
      return;
    }

    try {
      let updateData: any = {
        name,
      };

      if (subject.kind === SubjectKind.PREPARATION) {
        updateData.prep_price = prepPrice;
      }

      const response = await api.patch(`/subjects/${subject.id}/`, updateData);
      let updatedSubject = response.data;

      if (subject.kind === SubjectKind.LESSON) {
        const pricesToUpdate = lessonPrices.filter(
          (p) => p.price.trim() !== "" && p.id,
        );
        const updatePromises = pricesToUpdate.map((price) =>
          api.patch(`/prices/${price.id}/`, {
            price: price.price,
            duration: price.duration,
          }),
        );

        try {
          await Promise.all(updatePromises);

          const refreshedSubjectResponse = await api.get(
            `/subjects/${subject.id}/`,
          );
          updatedSubject = refreshedSubjectResponse.data;
        } catch (priceError) {
          console.error("Error updating prices:", priceError);
          toast.error("Greška prilikom ažuriranja cijena");
          Sentry.captureException(priceError);
        }
      }

      onSubjectUpdate(updatedSubject);
      toast.success("Predmet promijenjen");
    } catch (error) {
      console.log(error);
      toast.error("Greška prilikom promjene predmeta");
      Sentry.captureException(error);
    }
  };

  return (
    <Modal show={show} onHide={onHide}>
      <Modal.Header closeButton>
        <Modal.Title>Promijeni predmet</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <div className="mb-3">
          <label htmlFor="name" className="form-label">
            Naziv
          </label>
          <input
            type="text"
            className="form-control"
            id="name"
            value={name}
            onChange={handleNameChange}
          />
        </div>

        {subject.kind === SubjectKind.PREPARATION ? (
          <div className="mb-3">
            <label htmlFor="prepPrice" className="form-label">
              Cijena priprema
            </label>
            <div className="input-group">
              <input
                type="text"
                className="form-control"
                id="prepPrice"
                value={prepPrice}
                onChange={(e) => setPrepPrice(e.target.value)}
              />
              <div className="input-group-text">€</div>
            </div>
          </div>
        ) : (
          <div className="mb-3">
            <label className="form-label">Cijene poduka</label>
            {lessonPrices.map((item, index) => (
              <div className="input-group mb-2" key={item.duration}>
                <span className="input-group-text" style={{ width: "90px" }}>
                  {item.duration} min
                </span>
                <input
                  type="text"
                  className="form-control"
                  value={item.price}
                  onChange={(e) => handlePriceChange(index, e.target.value)}
                  placeholder="Cijena"
                />
                <div className="input-group-text">€</div>
              </div>
            ))}
          </div>
        )}

        <div className="mb-3">
          <SubjectPictureUpload
            subjectId={subject.id}
            initialImageUrl={subject.picture}
          />
        </div>
      </Modal.Body>
      <Modal.Footer>
        <Button variant="outline-primary" onClick={onHide}>
          Odustani
        </Button>
        <Button variant="primary" onClick={handleUpdate}>
          Promijeni
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

export default EditSubjectModal;
